import React, { Fragment } from 'react';
import { connect } from "react-redux";
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Form, Input, message } from 'antd';

import { hideModal, showModal } from "../actions/modalsActions";
import { userActions as authenticationActions } from "../actions/authenticationActions";
import {
  checkRegistrationEmail,
  REGISTRATION_EMAIL_STATUS,
  resendActivationLinkByEmail,
} from "../actions/userProfileAction";
import { getBrandId, getLanguage } from "../selectors/global";
import {
  getAuthenticationFailure,
  getAuthenticationFailureMsg,
  getLoy3AuthToken
} from '../selectors/authentication';
import { getMembershipInfo } from "../actions/sandos4UAction";
import { startLoading, finishLoading } from '../reducers/loading';
import Btn from '../components/common/btn';
import ModalActivationLinkSent from '../components/ModalActivationLinkSent';

const EMAIL_CHECK_DEBOUNCE_MS = 400;
const SUPPORT_EMAIL = 'sandos4u.mx@sandos.com';

// Login credentials errors use a generic message (UX option B) to avoid account enumeration.
// Account status messages (pending / unsubscribed / blocked) remain explicit for operational UX.
class LoginForm extends React.Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleCloseLinkSentModal = this.handleCloseLinkSentModal.bind(this)
    this.state = {
      loading: false,
      initialModalVisible: false,
      emailStatus: null,
      emailCheckLoading: false,
      isResending: false,
      credentialsError: false,
      showLinkSentModal: false,
    };
  }

  emailCheckTimer = null;
  emailCheckRequestId = 0;

  componentDidMount() {
    this._isMounted = true;
    this.resetForm();
  }

  componentDidUpdate(prevProps) {
    const prevType = prevProps.authFailureMsg && prevProps.authFailureMsg.type;
    const nextType = this.props.authFailureMsg && this.props.authFailureMsg.type;
    if (nextType === 'accountNotActivated' && nextType !== prevType) {
      this.setState({
        emailStatus: REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION,
        credentialsError: false,
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.emailCheckTimer) {
      clearTimeout(this.emailCheckTimer);
    }
  }

  closeLoginForm = () => {
    this.props.onClose();
  }

  resetForm() {
    this.props.form.resetFields();
  }

  clearCredentialsError = () => {
    if (this.state.credentialsError) {
      this.setState({ credentialsError: false });
    }
  };

  isEmailFormatValid = (email) => {
    if (!email || !email.trim()) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  isPendingVerification = () => {
    return this.state.emailStatus === REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION
      || (this.props.authFailureMsg && this.props.authFailureMsg.type === 'accountNotActivated');
  };

  isUnsubscribed = () => this.state.emailStatus === REGISTRATION_EMAIL_STATUS.UNSUBSCRIBED;

  isBlocked = () => this.state.emailStatus === REGISTRATION_EMAIL_STATUS.BLOCKED;

  hasBlockingEmailStatus = () => {
    return this.isPendingVerification() || this.isUnsubscribed() || this.isBlocked();
  };

  runEmailStatusCheck = (email) => {
    if (!this.isEmailFormatValid(email)) {
      this.setState({ emailStatus: null, emailCheckLoading: false });
      return Promise.resolve(null);
    }

    const requestId = ++this.emailCheckRequestId;
    this.setState({ emailCheckLoading: true });

    return this.props.checkRegistrationEmail(email)
      .then((result) => {
        if (requestId !== this.emailCheckRequestId) {
          return null;
        }
        this.setState({
          emailStatus: result.status,
          emailCheckLoading: false,
        });
        return result;
      })
      .catch(() => {
        if (requestId !== this.emailCheckRequestId) {
          return null;
        }
        this.setState({
          emailStatus: null,
          emailCheckLoading: false,
        });
        return null;
      });
  };

  scheduleEmailStatusCheck = (email) => {
    if (this.emailCheckTimer) {
      clearTimeout(this.emailCheckTimer);
    }
    this.emailCheckTimer = setTimeout(() => {
      this.runEmailStatusCheck(email);
    }, EMAIL_CHECK_DEBOUNCE_MS);
  };

  handleUsernameChange = (e) => {
    this.clearCredentialsError();
    this.scheduleEmailStatusCheck(e.target.value);
  };

  handlePasswordChange = () => {
    this.clearCredentialsError();
  };

  handleResendPendingVerification = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!this.isPendingVerification() && !this.isUnsubscribed()) {
      return;
    }
    const email = (this.props.form.getFieldValue('username') || '').trim();
    if (!email) {
      message.error(this.context.t('Failed to resend verification email'));
      return;
    }
    this.setState({ isResending: true });
    this.props.resendActivationLinkByEmail(email)
      .then(() => {
        this.setState({ isResending: false, showLinkSentModal: true });
      })
      .catch((err) => {
        message.error(err.message || this.context.t('Failed to resend verification email'));
        this.setState({ isResending: false });
      });
  };

  handleCloseLinkSentModal = () => {
    this.props.form.resetFields();
    this.setState({
      showLinkSentModal: false,
      emailStatus: null,
      emailCheckLoading: false,
      credentialsError: false,
      isResending: false,
    });
  };

  applyLoginFailure = async (errorType, username) => {
    if (errorType === 'accountNotActivated') {
      this.setState({
        emailStatus: REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION,
        credentialsError: false,
      });
      return;
    }

    const statusResult = await this.runEmailStatusCheck(username);
    const status = statusResult && statusResult.status;

    if (
      status === REGISTRATION_EMAIL_STATUS.PENDING_VERIFICATION
      || status === REGISTRATION_EMAIL_STATUS.UNSUBSCRIBED
      || status === REGISTRATION_EMAIL_STATUS.BLOCKED
    ) {
      this.setState({
        emailStatus: status,
        credentialsError: false,
      });
      return;
    }

    // Option B: generic credentials error (wrong password, unknown email, or other login failures).
    // Keep form values; stay in the login popup.
    this.setState({ credentialsError: true });
  };

  handleSubmit(e) {
    e.preventDefault();
    this.props.form.validateFields((err, values) => {
      if (err) {
        return;
      }

      this._isMounted && this.setState({
        loading: true,
        credentialsError: false,
      });
      this.props.loy3login(values.username, values.password, this.props.brandId, this.props.locale).then(async ({ success, errorType }) => {
        if (success) {
          this.props.history.push('/profile');
          return;
        }
        if (!this._isMounted) {
          return;
        }
        this.setState({ loading: false });
        await this.applyLoginFailure(errorType, values.username);
      });
    });
  };

  renderPendingVerificationHelp = () => {
    const { t } = this.context;
    const { isResending } = this.state;

    return (
      <div className="mt-1 text-sm">
        <p className="text-red-500">
          {t('This email has a registration in progress.')}{' '}
          <Btn
            className="text-main hover:text-highlight focus:outline-none p-0 inline"
            onClick={this.handleResendPendingVerification}
            disabled={isResending}
          >
            {isResending ? t('Sending') : t('Click to receive verification email.')}
          </Btn>
        </p>
      </div>
    );
  };

  renderUnsubscribedHelp = () => {
    const { t } = this.context;
    const { isResending } = this.state;

    return (
      <div className="email-status-help mt-1 text-sm">
        <p className="text-red-500">
          {t('This account was deactivated.')}{' '}
          <Btn
            className="email-status-cta focus:outline-none p-0 inline"
            onClick={this.handleResendPendingVerification}
            disabled={isResending}
          >
            {isResending ? t('Sending') : t('Click to receive a new verification email and reactivate it.')}
          </Btn>
        </p>
      </div>
    );
  };

  renderBlockedHelp = () => {
    const { t } = this.context;
    return (
      <div className="email-status-help mt-1 text-sm">
        <p>
          {t('We were unable to sign in to your account. Please contact our Support team at')}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="email-status-cta">
            {SUPPORT_EMAIL}
          </a>{' '}
          {t('to receive assistance.')}
        </p>
      </div>
    );
  };

  renderCredentialsError = () => (
    <p className="email-status-help mt-1 mb-2 text-sm text-red-500">
      {this.context.t('Incorrect email address or password. Please verify your information and try again.')}
    </p>
  );

  getUsernameHelp = () => {
    if (this.isPendingVerification()) {
      return this.renderPendingVerificationHelp();
    }
    if (this.isUnsubscribed()) {
      return this.renderUnsubscribedHelp();
    }
    if (this.isBlocked()) {
      return this.renderBlockedHelp();
    }
    return undefined;
  };

  render() {
    const { getFieldDecorator } = this.props.form;
    const hasBlockingEmailStatus = this.hasBlockingEmailStatus();

    return (
      <Fragment>
        <Form className="auth-login-form" onSubmit={this.handleSubmit} layout="vertical" hideRequiredMark>
          <Form.Item
            validateStatus={hasBlockingEmailStatus ? 'error' : undefined}
            help={this.getUsernameHelp()}
          >
            {getFieldDecorator('username', {
              rules: [
                {
                  required: true,
                  message: this.context.t('Please input your email!'),
                },
              ],
            })(
              <Input
                size="large"
                placeholder={this.context.t('Email address')}
                onChange={this.handleUsernameChange}
              />
            )}
          </Form.Item>
          <Form.Item>
            {getFieldDecorator('password', {
              rules: [
                {
                  required: true,
                  message: this.context.t('Please input your password!'),
                },
              ],
            })(
              <Input.Password
                size="large"
                placeholder={this.context.t('Password')}
                onChange={this.handlePasswordChange}
              />
            )}
          </Form.Item>
          {this.state.credentialsError && !hasBlockingEmailStatus && this.renderCredentialsError()}
          <Button
            type="primary"
            htmlType="submit"
            className="login-page__submit"
            disabled={this.state.loading || hasBlockingEmailStatus}
          >
            {this.state.loading ? this.context.t('Loading...') : this.context.t('Sign in now')}
          </Button>
        </Form>
        <ModalActivationLinkSent
          visible={this.state.showLinkSentModal}
          onClose={this.handleCloseLinkSentModal}
        />
      </Fragment>
    );
  }
}

LoginForm.propTypes = {
  brandId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locale: PropTypes.string,
  authToken: PropTypes.string,
  authFailure: PropTypes.bool,
  authFailureMsg: PropTypes.object,
  onClose: PropTypes.func,
  loy3login: PropTypes.func,
  loy3MembershipInfo: PropTypes.object,
  getMembershipInfo: PropTypes.func,
  checkRegistrationEmail: PropTypes.func.isRequired,
  resendActivationLinkByEmail: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

LoginForm.contextTypes = {
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authToken: getLoy3AuthToken(state),
  authFailure: getAuthenticationFailure(state),
  authFailureMsg: getAuthenticationFailureMsg(state),
  brandId: getBrandId(state),
  locale: getLanguage(state)
});

const mapDispatchToProps = (dispatch) => ({
  loy3login: (username, password, brandId, locale) => authenticationActions.loy3Login(username, password, brandId, locale)(dispatch),
  showModal: (payload) => dispatch(showModal(payload)),
  hideModal: (payload) => dispatch(hideModal(payload)),
  getMembershipInfo: (authToken, locale, brandId) => getMembershipInfo(authToken, locale, brandId)(dispatch),
  startLoading: (payload) => dispatch(startLoading(payload)),
  finishLoading: (payload) => dispatch(finishLoading(payload)),
  checkRegistrationEmail: (email) => checkRegistrationEmail(email)(dispatch),
  resendActivationLinkByEmail: (email) => resendActivationLinkByEmail(email)(dispatch),
});

LoginForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(LoginForm);

const WrappedRegistrationForm = Form.create({ name: 'loginForm' })(LoginForm);

export default withRouter(WrappedRegistrationForm);
